import type GlobalAudio from "./AudioStream";

import type LivePreview from "./LivePreview/LivePreview";

import Streaming, { StreamingState, type IProvider } from "./Streaming";

import CurrentWorkspace from "./Workspace";

import CloudRemote from "./CloudRemote";

import SelfHost from "./SelfHost";

import { cloudStreamService } from "../services/cloudStreamService";

import {
  SceneSyncChannel,
  type BroadcastWarningCode,
} from "../broadcast/sceneSync";

class BroadcastManager {
  private Streaming: Streaming;

  // Broadcast cloud en curso (para disparar el finalize de la grabación al parar).

  private lastCloudStreamId: string | null = null;

  private lastAutoRecord = false;

  constructor(streaming: Streaming) {
    this.Streaming = streaming;
  }

  private async mergeStream(
    livePreview: LivePreview,

    globalAudio: GlobalAudio,
  ): Promise<MediaStream> {
    const combinedStream = new MediaStream();

    // Video: en broadcast OSR NO hay track (NVENC captura el OSR; captureStream:false). Solo se

    // agrega si existe (compat con otros modos). El video lo provee NVENC vía el ring buffer.

    const videoStream = await livePreview.getStream();

    if (videoStream) {
      for (const track of videoStream.getVideoTracks()) {
        combinedStream.addTrack(track);
      }
    }

    const audioStream = globalAudio.AudioStreamDestination.stream;

    for (const track of audioStream.getAudioTracks()) {
      combinedStream.addTrack(track);
    }

    return combinedStream;
  }

  private async resolveRemoteConfig() {
    const cloudRemote = new CloudRemote();

    let remoteConfig = await cloudRemote.resolve();

    if (!remoteConfig) {
      const selfhost = new SelfHost();

      remoteConfig = await selfhost.resolve();
    }

    if (remoteConfig) {
      console.log("✅ Remote server configured:", remoteConfig.host);
    }

    return remoteConfig;
  }

  async start(
    livePreview: LivePreview,

    globalAudio: GlobalAudio,

    startSceneFn: () => Promise<void>,
  ) {
    if (!livePreview) {
      await startSceneFn();
    }

    const combinedStream = await this.mergeStream(livePreview, globalAudio);

    if (!combinedStream) {
      console.error("❌ Failed to create combined stream");

      return;
    }

    const userType = await CurrentWorkspace.getType();

    const isRecordingOnly = userType === "local_recording";

    if (!isRecordingOnly) {
      // Destinos y config remota son lecturas independientes: en paralelo (solo cuando se emite;
      // en grabación local no hacen falta).
      const [providers, remoteConfig] = await Promise.all([
        CurrentWorkspace.getProviders(),
        this.resolveRemoteConfig(),
      ]);

      const keroProviders = await this.resolveKeroProviders(providers);

      this.Streaming.setProviders(keroProviders);

      this.Streaming.setSelfhostConfig(remoteConfig);
    }

    await this.Streaming.broadcast(combinedStream);

    livePreview.updateOnLive(true);
  }

  /**
   * Modelo de destinos Cloudflare:
   * - LOCAL (`routing !== "cloud"`): Kero los emite directo (rtmp2sink, gratis, ∞).
   * - CLOUD (`routing === "cloud"`): el backend crea Live Outputs en Cloudflare (pago, plan) y
   *   devuelve UN ingest URL. Kero pushea el stream compuesto UNA vez ahí y Cloudflare fan-out.
   *
   * Se crea el stream Cloudflare (vía `/start`) cuando hay destinos cloud **o** cuando está activa
   * la grabación cloud (auto_record graba en Cloudflare → necesita pasar por el ingest, aunque
   * todos los destinos sean local). Resultado para Kero = destinos local + (si se creó stream CF)
   * un provider extra = el ingest de Cloudflare. 100% local sin grabación cloud → sin backend.
   */

  private async resolveKeroProviders(
    providers: IProvider[],
  ): Promise<IProvider[]> {
    const enabled = providers.filter((p) => p.enabled);

    const local = enabled.filter((p) => (p.routing ?? "local") !== "cloud");

    const cloud = enabled.filter((p) => (p.routing ?? "local") === "cloud");

    const autoRecord =
      typeof localStorage !== "undefined" &&
      localStorage.getItem("@Eriol/recording/cloud") === "true";

    // Reset del tracking del broadcast cloud anterior.

    this.lastCloudStreamId = null;

    this.lastAutoRecord = false;

    // Sin destinos cloud y sin grabación cloud → 100% local (comportamiento original).

    if (cloud.length === 0 && !autoRecord) {
      return providers;
    }

    // La parte cloud (Live Outputs / grabación VOD) requiere plan: el backend rechaza a

    // los usuarios sin suscripción activa (400). Esa parte NUNCA debe tumbar la emisión

    // local (Kero, gratis) — que es justamente lo que puede hacer un usuario sin plan.

    // Si el /start falla, degradamos a local-only y avisamos, en vez de propagar el error.

    let result: Awaited<ReturnType<typeof cloudStreamService.startBroadcast>>;

    try {
      result = await cloudStreamService.startBroadcast({
        autoRecord,

        cloudDestinations: cloud.map((p) => ({
          name: p.name,

          endpoint: p.endpoint,

          key: p.key,
        })),
      });
    } catch (e) {
      console.error(
        "Cloud broadcast falló; se continúa solo con los destinos locales:",

        e,
      );

      // Aviso NO-fatal al control window (el broadcast local sigue). Mandamos un
      // CODE (no texto) para que el control lo traduzca vía i18n al idioma del user.
      // Distingue "solo se perdió la grabación cloud" de "no salen los destinos cloud".

      const code: BroadcastWarningCode =
        cloud.length > 0 ? "cloudDestinationsFailed" : "cloudRecordingFailed";

      try {
        new SceneSyncChannel().post({ type: "broadcast-warning", code });
      } catch {
        /* sin BroadcastChannel (editor) → el aviso es best-effort */
      }

      // Degradación: emitir SOLO los destinos locales habilitados. Los cloud quedan fuera

      // (no hay ingest de Cloudflare al que empujarlos).

      return local;
    }

    // Trackear el stream cloud SIEMPRE (para liberar el cupo de concurrencia al

    // parar vía /stop) + el flag de grabación (para el finalize del VOD).

    this.lastCloudStreamId = result.stream?.id ?? null;

    this.lastAutoRecord = autoRecord;

    // Provider extra: el ingest de Cloudflare. Kero pushea acá UNA vez; Cloudflare hace el ABR,

    // el fan-out a los Live Outputs (destinos cloud) y/o la grabación (auto_record). Sin encoding

    // override → una sola rendition. Se agrega siempre que se haya creado el stream (hay ingest).

    const cloudIngest: IProvider[] = result.ingestUrl
      ? [
          {
            name: "Cloudflare",

            endpoint: result.ingestUrl,

            key: result.rtmpStreamKey ?? "",

            enabled: true,

            routing: "cloud",
          },
        ]
      : [];

    return [...local, ...cloudIngest];
  }

  async stop(livePreview: LivePreview | null) {
    // Tomar el resumen de salud del encoder ANTES de cerrar (Fase 5.1).

    const health = this.Streaming.consumeBroadcastHealth();

    this.Streaming.stop();

    if (livePreview) {
      livePreview.updateOnLive(false);
    }

    // Cerrar el broadcast cloud (si lo hubo).

    const streamId = this.lastCloudStreamId;

    const wasAutoRecord = this.lastAutoRecord;

    this.lastCloudStreamId = null;

    this.lastAutoRecord = false;

    if (streamId) {
      // Liberar el cupo de concurrencia cloud (status → stopped). Fire-and-forget.

      cloudStreamService.stopBroadcast(streamId).catch((e) => {
        console.error("stopBroadcast falló:", e);
      });

      // Reportar la salud del encoder a la sesión (bitrate/dropped/renditions). Fire-and-forget.

      if (health) {
        cloudStreamService.reportHealth(streamId, health).catch((e) => {
          console.error("reportHealth falló (no-fatal):", e);
        });
      }

      // Grabación cloud activa → finalize del VOD (CF getRecordings → enableDownload → R2 → Vod).

      // El VOD aparece en la biblioteca cuando Cloudflare termina de renderizar el MP4.

      if (wasAutoRecord) {
        cloudStreamService.finalizeRecording(streamId).catch((e) => {
          console.error(
            "finalizeRecording falló (la grabación cloud puede tardar):",

            e,
          );
        });
      }
    }
  }

  async updateScreenshot(path: string) {
    await this.Streaming.updateScreenshot(path);
  }

  isStreaming() {
    return this.Streaming.getState() === StreamingState.STARTED;
  }

  getStreamingState() {
    return this.Streaming.getState();
  }
}

export default BroadcastManager;
